import React, { useCallback, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { useNavigate } from "react-router-dom";
import TabsNav from "../../components/TabsNav";
import { GridColDef, DataGrid } from "@mui/x-data-grid";
import {
  DataGridListStyle,
  HtmlTooltip,
  ContentStyle,
  Hints,
  Note,
} from "../../Theme";
import { YSButton } from "../../uikit/YSButton";
import { v4 as uuid } from "uuid";
import LockIcon from "@mui/icons-material/Lock";
import { MedicationHxFilters } from "../../components/MedicationHxFilters";
import { DialogPopup } from "../../components/DialogPopup";
import { useDispatch, useSelector } from "react-redux";
import { loadingMedicationHistory } from "../../Redux/Slice/MedicationHistorySlice";
import YSSnackBar from "../../uikit/YSSnackBar";
import HintIcon from "@mui/icons-material/FiberManualRecord";
import NoRecords from "../../components/NoRecords";
import {
  DrugTextNTooltip,
  MedicationHistoryNotes,
  PrintClick,
  TextNTooltip,
} from "../../components/Content";
import ErrorOverlay from "../../components/ErrorOverlay";
import {
  ConfirmReprintRefaxAPI,
  MedHistoryPrintCopy,
  MedicationHistoryCancelRx,
  NdcHistory,
} from "../../Apis/AuthApiServices";
import { REPORT_BASE_URL } from "../../Apis/Constants";
import { useOnBehalfContext } from "../../context/OnbehalfContext";
import { Box } from "@mui/material";
import { isDocereeAdsEnabled } from "../../utils/envConfig";

export default function MedicationHistory(prop: any) {
  const patientId: any = sessionStorage.getItem("patientId");
  const { onBehalfContext }: any = useOnBehalfContext();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const patientState = useSelector((state: any) => {
    return { ...state?.PatientDetailsSlice };
  }); //redux data
  const UserServiceState = useSelector((state: any) => {
    return { ...state?.UserServicesSlice?.ServicesData };
  }); //redux data
  const MedHistoryData = useSelector((state: any) => {
    return { ...state?.MedicationHistorySlice };
  }); //To get data from redux store state
  const [isPrinting, setIsPrinting] = useState(false);
  const [printPopup, setPrintPopup] = useState(false);
  const [printUrl, setPrintUrl] = useState("");
  const [load, setLoad] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [selectionData, setSelectionData] = useState<any>([]);
  const [mxvalue, setMXValue] = useState("active");
  const [selectedDrugIds, setSelectedDrugIds] = useState([]);
  const [StateData, SetStateData] = useState<any>({
    message: "",
    open: false,
    variant: "",
  });
  const [showMenu, setShowMenu] = React.useState(true);
  const [rowCountState, setRowCountState] = useState(
    MedHistoryData?.Pagination?.totalItems || 0
  );
  const handleShowPrintPopupClick = () => {
    setPrintPopup(true);
  };
  const handleHidePrintPopupClick = () => {
    setPrintPopup(false);
  };
  const getPrintCopy = useCallback(async (rowData: any) => {
    try {
      const bodyData = {
        patientId: rowData?.personId,
        userDetails: {
          doctorId: localStorage.getItem("doctorId"),
          onBehalfDoctorId: localStorage.getItem("onBehalf") ?? "",
          selectedClinicId: sessionStorage.getItem("clinicId"),
        },
        prescriptionIds: [rowData?.personDrugId],
      };
      const result = await MedHistoryPrintCopy(bodyData);
      if (result?.data?.digitalRxStatusCode === 200) {
        setPrintUrl(result?.data?.response[0]);
        handleShowPrintPopupClick();
      } else {
        setPrintUrl("");
        SetStateData((prevState: any) => {
          return {
            ...prevState,
            message: result?.data?.digitalRxStatusMessage,
            variant: "error",
            open: true,
          };
        });
      }
    } catch (error) {
      setPrintUrl("");
      SetStateData((prevState: any) => {
        return {
          ...prevState,
          message: "Try again after sometime!",
          variant: "error",
          open: true,
        };
      });
    }
  }, []);
  const columnsMedicationHistory = useMemo<GridColDef[]>(
    () => [
      {
        field: "description",
        headerName: "Drug Description",
        width: 150,
        cellClassName: "w20",
        headerClassName: "w20",
        renderCell: ({ row }) => (
          <DrugTextNTooltip
            row={row}
            onClick={() => {
              if (row?.showPrintTooltip) return;
              if (
                !row?.obseletRx &&
                UserServiceState?.profileGroup !== 11 &&
                !row?.freeTextRx &&
                (!sessionStorage.getItem("freeTextRxTabs") ||
                  JSON.parse(sessionStorage.getItem("freeTextRxTabs")!)[0]
                    ?.drugName === "")
              ) {
                navigate("/newrxpad", {
                  state: {
                    rowData: row,
                    key: "MHDrugPop",
                  },
                });
              } else if (
                row?.freeTextRx &&
                (!sessionStorage.getItem("rxTabs") ||
                  JSON.parse(sessionStorage.getItem("rxTabs")!)[0]?.drugName ===
                    "")
              ) {
                navigate("/freetextmedication", {
                  state: {
                    rowData: row,
                    key: "FreeTextMHPop",
                  },
                });
              } else {
                SetStateData((prevState: any) => {
                  return {
                    ...prevState,
                    message:
                      "Please don't select the regular and free-text prescriptions together.",
                    variant: "error",
                    open: true,
                  };
                });
              }
            }}
            color={
              !row?.showPrintTooltip &&
              !row?.obseletRx &&
              UserServiceState?.profileGroup !== 11
                ? "primary"
                : ""
            }
            needPharmacy={true}
            showInactivationDetails={row?.showInactivationDetails}
            showPrintTooltip={row?.showPrintTooltip}
          />
        ),
      },
      {
        field: "sig",
        headerName: "SIG",
        width: 150,
        cellClassName: "w20",
        headerClassName: "w20",
        renderCell: ({ row }) => <TextNTooltip Text={row?.sig} />,
      },
      {
        field: "createdDate",
        headerName: "Date",
        type: "date",
        width: 100,
        cellClassName: "w12",
        headerClassName: "w12",
        renderCell: ({ row }) => (
          <Typography component="div" variant="body2">
            {row.obseletRx ? "" : row.createdDate}
          </Typography>
        ),
      },
      {
        field: "rxType",
        headerName: "Type",
        width: 70,
        cellClassName: "w8",
        headerClassName: "w8",
      },
      {
        field: "Status",
        headerName: "Status",
        width: 100,
        cellClassName: "w8",
        headerClassName: "w8",
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <HtmlTooltip
            placement="right"
            className={
              row.prescriptionHelperColorCode === 0
                ? "green"
                : row.prescriptionHelperColorCode === 1
                ? "orange"
                : row.prescriptionHelperColorCode === 2
                ? "red"
                : ""
            }
            arrow
            title={row.prescriptionStatusHelperMessage}
          >
            <IconButton className="tooltip">
              <Typography component="div" variant="body2">
                {row.prescriptionStatus}
              </Typography>
            </IconButton>
          </HtmlTooltip>
        ),
      },
      {
        field: "doctorName",
        headerName: "Physician",
        width: 100,
        cellClassName: "w18",
        headerClassName: "w18",
        renderCell: ({ row }) => (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            width="100%"
            overflow="hidden"
          >
            <Typography component="div" variant="body2">
              {`${row.doctorFirstName} ${row.doctorLastName}`.length > 13 ? (
                <>
                  <HtmlTooltip
                    placement="right"
                    arrow
                    title={`${row.doctorFirstName} ${row.doctorLastName}`}
                  >
                    <Typography component="div" variant="body2">
                      {`${row.doctorFirstName} ${row.doctorLastName}`.substring(
                        0,
                        13
                      ) + "..."}
                    </Typography>
                  </HtmlTooltip>
                </>
              ) : (
                `${row.doctorFirstName} ${row.doctorLastName}`
              )}
            </Typography>
            {row.doctorNote && (
              <MedicationHistoryNotes
                notes={row?.doctorNote ? row?.doctorNote : "Not Available"}
              />
            )}
          </Box>
        ),
      },
      {
        field: "activeFlag",
        headerName: "Active",
        width: 60,
        cellClassName: "w8",
        headerClassName: "w8",
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          return <>{row.activeFlag === true ? <>Y</> : <>N</>}</>;
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 60,
        cellClassName: "w8 warning",
        headerClassName: "w8",
        renderCell: ({ row }) => (
          <PrintClick onClick={() => getPrintCopy(row)} />
        ),
      },
    ],
    [UserServiceState?.profileGroup, getPrintCopy, navigate]
  );
  const handleShowMenuClick = () => {
    setShowMenu(!showMenu);
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMXValue(event.target.value);
    setPage(0);
  };
  const getMedicationHistoryData = useCallback(
    (nextPage: number, mxvalue: string) => {
      let dataOfPayload = {
        pageNumber: nextPage,
        drugStatus: mxvalue,
        patientId: patientId,
      };

      dispatch(loadingMedicationHistory(dataOfPayload));
    },
    [dispatch, patientId]
  );
  const getNdcHistory = async () => {
    if (!isDocereeAdsEnabled()) {
      return;
    }
    try {
      const params: any = new URLSearchParams();
      params.append("patientId", sessionStorage.getItem("patientId"));
      const result: any = await NdcHistory(params);
      if (result?.data?.digitalRxStatusCode === 200) {
        const customEvent3 = new CustomEvent("docreePrescriptionHistory", {
          detail: result?.data?.response,
        });
        document.dispatchEvent(customEvent3);
      }
    } catch (error) {}
  };
  const gotoConfirmCancellationorPrecription = async (mode: any) => {
    try {
      setLoad(true);
      const params = new URLSearchParams({
        prescriptionMode: mode,
      });
      const bodyData = {
        patientId: patientId,
        userDetails: {
          doctorId:
            UserServiceState?.profileGroup > 6
              ? localStorage.getItem("SupervisorDoctorId")
              : localStorage.getItem("doctorId"),
          onBehalfDoctorId: localStorage.getItem("onBehalf") ?? "",
          selectedClinicId: sessionStorage.getItem("clinicId"),
        },
        prescriptionIds: selectedDrugIds,
      };
      const result = await MedicationHistoryCancelRx(params, bodyData);
      if (result?.data?.digitalRxStatusCode === 200) {
        if (mode === "cancelrx") {
          navigate("/cancelprescriptions", {
            state: result?.data?.response[0]?.prescriptionFormList,
          });
        } else if (mode === "newrx") {
          if (result?.data?.response[0]?.popUpFlag?.freeText) {
            navigate("/freetextmedication/summary", {
              state: "confirmFreeTextMhList",
            });
            sessionStorage.setItem(
              "locationData",
              JSON.stringify(result?.data?.response[0])
            );
            sessionStorage.setItem(
              "freeTextRxTabs",
              JSON.stringify(result?.data?.response[0]?.prescriptionFormList)
            );
          } else {
            navigate("/newrxpad/summary", {
              state: "confirmMhList",
            });
            sessionStorage.setItem(
              "locationData",
              JSON.stringify(result?.data?.response[0])
            );
            sessionStorage.setItem(
              "rxTabs",
              JSON.stringify(result?.data?.response[0]?.prescriptionFormList)
            );
          }
        }
        setLoad(false);
      } else {
        SetStateData((prevState: any) => {
          return {
            ...prevState,
            message: result?.data?.digitalRxStatusMessage,
            variant: "error",
            open: true,
          };
        });
        setLoad(false);
      }
    } catch (eror) {
      SetStateData((prevState: any) => {
        return {
          ...prevState,
          message: "Something went wrong! Please try again after sometime.",
          variant: "error",
          open: true,
        };
      });
      setLoad(false);
    }
  };
  const gotoConfirmReprintRefax = async () => {
    try {
      setLoad(true);
      const bodyData = {
        patientId: patientId,
        userDetails: {
          doctorId:
            UserServiceState?.profileGroup > 6
              ? localStorage.getItem("SupervisorDoctorId")
              : localStorage.getItem("doctorId"),
          onBehalfDoctorId: localStorage.getItem("onBehalf") ?? "",
          selectedClinicId: sessionStorage.getItem("clinicId"),
        },
        prescriptionIds: selectedDrugIds,
      };
      const result = await ConfirmReprintRefaxAPI(bodyData);
      if (result?.data?.digitalRxStatusCode === 200) {
        navigate("/confirmreprintrefax", {
          state: result?.data?.response[0],
        });
        setLoad(false);
      } else {
        SetStateData((prevState: any) => {
          return {
            ...prevState,
            message: result?.data?.digitalRxStatusMessage,
            variant: "error",
            open: true,
          };
        });
        setLoad(false);
      }
    } catch (error) {
      SetStateData((prevState: any) => {
        return {
          ...prevState,
          message: "Something went wrong! Please try again after sometime.",
          variant: "error",
          open: true,
        };
      });
      setLoad(false);
    }
  };
  useEffect(() => {
    function handleBeforePrint() {
      flushSync(() => {
        setIsPrinting(true);
      });
    }

    function handleAfterPrint() {
      setIsPrinting(false);
    }

    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);
    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, []);
  useEffect(() => {
    getNdcHistory();
  }, []);
  useEffect(() => {
    getMedicationHistoryData(page, mxvalue);
  }, [getMedicationHistoryData, mxvalue, page]);
  useEffect(() => {
    setRowCountState((prevRowCountState: any) =>
      MedHistoryData?.Pagination?.totalItems !== undefined
        ? MedHistoryData?.Pagination?.totalItems
        : prevRowCountState
    );
  }, [MedHistoryData?.Pagination?.totalItems, setRowCountState]);
  if (load)
    return (
      <Grid
        container
        justifyContent={"center"}
        display={"flex"}
        alignItems="center"
        height={"100vh"}
      >
        {" "}
        <CircularProgress color="primary" />
      </Grid>
    );
  return (
    <>
      <TabsNav />{" "}
      <ContentStyle>
        {patientState?.AllPatientDetails?.legacyMedicationHistoryAvailable && (
          <Grid
            container
            display="flex"
            xs={12}
            spacing={0}
            alignItems="top"
            justifyContent="center"
          >
            <Note
              style={{
                marginBottom: 2,
                border: "1.5px solid #f0ad6b",
                borderRadius: 5,
                paddingLeft: 20,
                paddingRight: 20,
              }}
            >
              <Typography
                variant="body2"
                component="div"
                fontWeight={600}
                textAlign={"center"}
              >
                <Typography component="span" variant="body2" fontWeight={500}>
                  GO TO LEGACY MEDICATION HISTORY:{" "}
                </Typography>{" "}
                <Typography component="span" color="primary" variant="body2">
                  ---{">"}{" "}
                  <Box
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      cursor: "pointer",
                      position: "relative",
                      fontWeight: 600,
                    }}
                    onClick={() => {
                      navigate("/medicationshistory/legacy-history");
                    }}
                  >
                    CLICK HERE
                  </Box>
                </Typography>{" "}
              </Typography>
            </Note>
          </Grid>
        )}
        <Grid
          container
          item
          xs={12}
          spacing={0}
          alignItems="top"
          justifyContent="space-between"
          marginBottom={2}
        >
          <Grid item xs={4}>
            <Typography
              variant="subtitle2"
              color={"primary"}
              component={"div"}
              fontWeight={600}
            >
              {mxvalue === "all" && <>Complete History </>}
              {mxvalue === "active" && <> Active Medication(s) </>}
              {mxvalue === "inactive" && <> Inactive Medication(s) </>}
            </Typography>
          </Grid>

          <Grid item xs={4}>
            <MedicationHxFilters
              showMenu={showMenu}
              handleShowMenuClick={handleShowMenuClick}
              mxvalue={mxvalue}
              setMXValue={setMXValue}
              handleChange={handleChange}
              disabled={UserServiceState?.profileGroup !== 11 ? false : true}
            />
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={12}
          spacing={1}
          alignItems="top"
          justifyContent="center"
        >
          <Grid item xs={12}>
            {isPrinting ? (
              <DataGridListStyle
                className={mxvalue === "inactive" ? "inactive " : ""}
              >
                <DataGrid
                  autoHeight
                  columns={columnsMedicationHistory}
                  checkboxSelection
                  components={{
                    NoRowsOverlay: NoRecords,
                    NoResultsOverlay: NoRecords,
                    ErrorOverlay: ErrorOverlay,
                  }}
                  error={MedHistoryData?.error || undefined}
                  disableSelectionOnClick
                  getRowId={(row) => row.personDrugId ?? uuid()}
                  headerHeight={36}
                  rowHeight={40}
                  loading={MedHistoryData.isLoading}
                  onPageChange={(newPage) => {
                    setPage(newPage);
                  }}
                  paginationMode="server"
                  pageSize={pageSize}
                  onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                  page={page}
                  rows={MedHistoryData?.Items ?? []}
                  rowsPerPageOptions={[20]}
                  rowCount={rowCountState}
                  sortingOrder={["asc", "desc", null]}
                  onSelectionModelChange={(ids: any) => {
                    const selectedIDs = new Set(ids);
                    const selectedRows = MedHistoryData?.Items?.filter(
                      (row: any) => selectedIDs.has(row.personDrugId)
                    );

                    setSelectionData(selectedRows);
                    setSelectedDrugIds(ids);
                  }}
                  getRowClassName={(params) => {
                    if (params.row.freeTextRx) {
                      return "FTP";
                    } else if (params.row.erroredRx) {
                      return "EP";
                    } else if (params.row.obseletRx) {
                      return "OP";
                    }
                    return "";
                  }}
                />
              </DataGridListStyle>
            ) : (
              <DataGridListStyle
                className={mxvalue === "inactive" ? "inactive " : ""}
                style={{ height: 500, overflow: "auto" }}
              >
                <DataGrid
                  columns={columnsMedicationHistory}
                  checkboxSelection
                  components={{
                    NoRowsOverlay: NoRecords,
                    NoResultsOverlay: NoRecords,
                    ErrorOverlay: ErrorOverlay,
                  }}
                  error={MedHistoryData?.error || undefined}
                  disableSelectionOnClick
                  getRowId={(row) => row.personDrugId ?? uuid()}
                  headerHeight={36}
                  rowHeight={40}
                  loading={MedHistoryData.isLoading}
                  onPageChange={(newPage) => {
                    setPage(newPage);
                  }}
                  paginationMode="server"
                  pageSize={pageSize}
                  onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                  page={page}
                  rows={MedHistoryData?.Items ?? []}
                  rowsPerPageOptions={[20]}
                  rowCount={rowCountState}
                  sortingOrder={["asc", "desc", null]}
                  onSelectionModelChange={(ids: any) => {
                    const selectedIDs = new Set(ids);
                    const selectedRows = MedHistoryData?.Items?.filter(
                      (row: any) => selectedIDs.has(row.personDrugId)
                    );

                    setSelectionData(selectedRows);
                    setSelectedDrugIds(ids);
                  }}
                  getRowClassName={(params) => {
                    if (params.row.freeTextRx) {
                      return "FTP";
                    } else if (params.row.erroredRx) {
                      return "EP";
                    } else if (params.row.obseletRx) {
                      return "OP";
                    }
                    return "";
                  }}
                />
              </DataGridListStyle>
            )}
          </Grid>
          <Grid item xs={12} textAlign="center" className="btns">
            <YSButton
              onClick={() => {
                if (
                  selectedDrugIds.length === 1 &&
                  selectionData[0]?.activeFlag
                ) {
                  navigate("/drugupdate", {
                    state: {
                      selectionData: selectionData,
                      selectedDrugIds: selectedDrugIds,
                    },
                  });
                } else {
                  SetStateData((prevState: any) => {
                    return {
                      ...prevState,
                      message:
                        "Select only one drug for update & Inactive drug can't be update.",
                      variant: "error",
                      open: true,
                    };
                  });
                }
              }}
              className=""
              children={"Update Drug"}
              size={"large"}
              type="button"
              color="primary"
              variant="outlined"
              disabled={
                onBehalfContext?.id
                  ? false
                  : UserServiceState?.profileGroup > 6
                  ? true
                  : false
              }
              startIcon={
                onBehalfContext?.id
                  ? null
                  : UserServiceState?.profileGroup > 6 && <LockIcon />
              }
            />{" "}
            <YSButton
              onClick={() => {
                const everyFlag = selectionData.every(
                  (item: any) => item?.activeFlag === true
                );
                if (selectedDrugIds.length >= 1 && everyFlag) {
                  navigate("/medicationinactivation", {
                    state: {
                      selectionData: selectionData,
                      selectedDrugIds: selectedDrugIds,
                    },
                  });
                } else {
                  SetStateData((prevState: any) => {
                    return {
                      ...prevState,
                      message:
                        "Select at least one drug for deactivation & Select active drugs for inactivation",
                      variant: "error",
                      open: true,
                    };
                  });
                }
              }}
              className=""
              children={"Inactivate Drug(s)"}
              size={"large"}
              type="button"
              color="primary"
              variant="outlined"
              disabled={
                onBehalfContext?.id && UserServiceState?.profileGroup !== 11
                  ? false
                  : UserServiceState?.profileGroup > 9
                  ? true
                  : false
              }
              startIcon={
                onBehalfContext?.id && UserServiceState?.profileGroup !== 11
                  ? null
                  : UserServiceState?.profileGroup > 9 && <LockIcon />
              }
            />{" "}
            <YSButton
              onClick={() => {
                gotoConfirmReprintRefax();
              }}
              className=""
              children={"Confirm Reprint/Refax"}
              size={"large"}
              type="button"
              color="primary"
              variant="outlined"
              disabled={
                onBehalfContext?.id
                  ? false
                  : UserServiceState?.profileGroup > 6
                  ? true
                  : false
              }
              startIcon={
                onBehalfContext?.id
                  ? null
                  : UserServiceState?.profileGroup > 6 && <LockIcon />
              }
            />{" "}
            <YSButton
              onClick={() => {
                gotoConfirmCancellationorPrecription("cancelrx");
              }}
              className=""
              children={"Confirm Cancellation(s)"}
              size={"large"}
              type="button"
              color="primary"
              variant="outlined"
              disabled={
                onBehalfContext?.id && UserServiceState?.profileGroup !== 11
                  ? false
                  : UserServiceState?.profileGroup > 9
                  ? true
                  : false
              }
              startIcon={
                onBehalfContext?.id && UserServiceState?.profileGroup !== 11
                  ? null
                  : UserServiceState?.profileGroup > 9 && <LockIcon />
              }
            />{" "}
            <YSButton
              onClick={() => {
                gotoConfirmCancellationorPrecription("newrx");
              }}
              className=""
              children={"Confirm Prescription(s)"}
              size={"large"}
              type="button"
              color="primary"
              variant="outlined"
              disabled={
                onBehalfContext?.id && UserServiceState?.profileGroup !== 11
                  ? false
                  : UserServiceState?.profileGroup > 9
                  ? true
                  : false
              }
              startIcon={
                onBehalfContext?.id && UserServiceState?.profileGroup !== 11
                  ? null
                  : UserServiceState?.profileGroup > 9 && <LockIcon />
              }
            />{" "}
          </Grid>
          <Grid>
            <Hints>
              {" "}
              <Typography
                component="label"
                variant="body2"
                fontWeight={600}
                className="ftp"
              >
                <HintIcon /> <div> Free Text Prescriptions</div>
              </Typography>{" "}
              <Typography
                component="label"
                variant="body2"
                fontWeight={600}
                className="efp"
              >
                <HintIcon /> <div> Error/Failed Prescriptions</div>
              </Typography>
              <Typography
                component="label"
                variant="body2"
                fontWeight={600}
                className="op"
              >
                <HintIcon /> <div> Obsolete Prescriptions</div>
              </Typography>
              <Typography
                component="label"
                variant="body2"
                fontWeight={600}
                className="op"
              >
                <b>* </b> <div> Updated Drug.</div>
              </Typography>
              <Typography
                component="label"
                variant="body2"
                fontWeight={600}
                className="op"
              >
                <b>** </b> <div> Reprint/Refax.</div>
              </Typography>
            </Hints>
          </Grid>
        </Grid>

        <DialogPopup
          windowsize={"md"}
          show={printPopup}
          handleShowPopupClick={handleShowPrintPopupClick}
          handleHidePopupClick={handleHidePrintPopupClick}
          close={true}
        >
          <Grid container spacing={0}>
            <Grid item xs={12} md={4}></Grid>
            <Grid item xs={12} md={4} textAlign="center"></Grid>
            <Grid
              item
              xs={12}
              textAlign="center"
              marginTop={1}
              className="btns"
            >
              <Grid item xs={12}>
                <iframe
                  src={`${REPORT_BASE_URL}${printUrl}`}
                  height="450px"
                  width="860px"
                  title="PrintReport"
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogPopup>
        <YSSnackBar
          message={StateData.message}
          variant={StateData.variant}
          open={StateData.open}
          handleClose={() => {
            SetStateData((prevState: any) => {
              return { ...prevState, open: false };
            });
          }}
        />
      </ContentStyle>
    </>
  );
}
